import { useEffect, type ReactNode } from "react";
import { Nav } from "@/components/Nav";
import styles from "./privacy.module.css";

const PAGE_TITLE = "Privacy Policy";

/**
 * The privacy policy every Company's enquiry form links to.
 *
 * Every claim here must be one the code keeps true. It said data was deleted
 * after 7 years, and nothing deletes anything; it named payments and
 * contractors, which are the RMS's and never reach intake; and it said
 * nothing about calls being recorded or answered by AI. The retention line
 * says what happens today. When a deletion schedule exists, the number goes
 * here with it, and not before.
 *
 * It says what is collected and who it goes to, not how the product is
 * built: the providers behind it are one line, unnamed. Laid out like the
 * apex page (`home.tsx`), the other page HireOS speaks for itself on.
 */
export default function PrivacyPage() {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <div className={styles.page}>
      <Nav />

      <main className={styles.main}>
        <div className={styles.prose}>
          <h1 className={styles.title}>Privacy policy</h1>

          <Section title="Who we are">
            <p>
              HireOS takes enquiries for the rental company you're contacting.
              That company decides what happens with your enquiry, and we handle
              it for them.
            </p>
            <p className={styles.followingParagraph}>
              HireOS is run by HireOS Limited, a New Zealand company. When this
              policy says "we", it means HireOS Limited.
            </p>
          </Section>

          <Section title="What we collect">
            {/* Each item is a <p> so it takes the theme's body text, as every
                other line on the page does; a bare <li> inherits the page's
                larger root size instead. */}
            <ul className={styles.bullets}>
              <li>
                <p>Your name, email, phone number and organisation.</p>
              </li>
              <li>
                <p>
                  Your event details, venue address, your answers and anything
                  you write in.
                </p>
              </li>
              <li>
                <p>Files you upload, like a tech rider or site plan.</p>
              </li>
              <li>
                <p>
                  If you call, your phone number, a recording of the call and a
                  transcript of it.
                </p>
              </li>
            </ul>
          </Section>

          <Section title="Calls">
            <p>
              Calls are recorded and transcribed so the company can see exactly
              what was said. If nobody picks up, an AI receptionist may answer
              and take down your enquiry.
            </p>
            <p className={styles.followingParagraph}>
              The demo call on this site is different: it isn't recorded, and it
              doesn't reach a real rental company. We keep a transcript of it so
              we can see how the demo went.
            </p>
          </Section>

          <Section title="How we use it">
            <p>
              To get your enquiry to the rental company and draft a quote for it.
              We don't sell your information or use it for marketing.
            </p>
            <p className={styles.followingParagraph}>
              We use service providers to run HireOS, some of them overseas. They
              only use your information to provide that service.
            </p>
          </Section>

          <Section title="How long we keep it">
            <p>
              We keep your information while the rental company uses HireOS. If
              you want it deleted sooner, email us and we'll delete it.
            </p>
          </Section>

          <Section title="If you use the HireOS app">
            <p>
              This part is for the staff of a rental company who sign in to
              HireOS, on the web or in the app. It's about your own login, not
              the enquiries you read.
            </p>
            <ul className={`${styles.bullets} ${styles.followingParagraph}`}>
              <li>
                <p>
                  Signing in: your email address and password. Your password
                  is held by our sign-in provider, and we never see it.
                </p>
              </li>
              <li>
                <p>
                  How you use HireOS: the pages you open, what you tap and a
                  recording of the screen, tied to your email and your
                  company. All text and everything you type is hidden before
                  it's sent, so no enquiry or customer detail goes with it. We use it to see what works and fix
                  what doesn't.
                </p>
              </li>
              <li>
                <p>
                  Notifications: a token from Apple or Google for each phone
                  you sign in on, so we can ring your phone for a call and
                  tell you about a text. It's removed when you log out.
                </p>
              </li>
              <li>
                <p>
                  The microphone, when you ask for it: to talk on a call, and
                  to record a greeting for your number in Settings → Phone.
                  Calls taken in the app are recorded and transcribed like any
                  other call, and a greeting you record is played to callers.
                </p>
              </li>
            </ul>
            <p className={styles.followingParagraph}>
              The app doesn't read your contacts, your location or anything
              else on your phone.
            </p>
            <p className={styles.followingParagraph}>
              To delete your login, delete it from Settings in the app, or
              email{" "}
              <a href="mailto:hello@loony.io" className={styles.link}>
                hello@loony.io
              </a>
              . Your login goes straight away. The enquiries and calls belong
              to your company and stay with it.
            </p>
          </Section>

          <Section title="Your rights">
            <p>
              Under the Privacy Act 2020 you can ask to see the information we
              hold about you, and ask us to correct or delete it. Email{" "}
              <a href="mailto:hello@loony.io" className={styles.link}>
                hello@loony.io
              </a>
              . If you're not happy with how we handle it, you can complain to the{" "}
              <a href="https://www.privacy.org.nz" className={styles.link}>
                Privacy Commissioner
              </a>
              .
            </p>
          </Section>

          <p className={styles.updated}>Last updated 18 October 2026</p>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>{title}</h2>
      {children}
    </section>
  );
}
